import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, DollarSign } from "lucide-react";
import api from "../lib/api";
import CarCard from "../components/CarCard";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/I18nContext";

const BODY_TYPES = ["Sedan", "SUV", "Hatchback", "Pickup", "Coupe", "Convertible", "Van"];
const FUELS = ["Petrol", "Diesel", "Hybrid", "Electric"];
const TRANSMISSIONS = ["Automatic", "Manual"];
const CONDITIONS = ["New", "Used", "Excellent"];
const LOCATIONS = ["Phnom Penh", "Siem Reap", "Sihanoukville", "Battambang"];
const PRICE_PILLS = [
  { label: "Under $20k", min: "", max: "20000" },
  { label: "$20k – $40k", min: "20000", max: "40000" },
  { label: "$40k – $60k", min: "40000", max: "60000" },
  { label: "$60k – $100k", min: "60000", max: "100000" },
  { label: "$100k+", min: "100000", max: "" },
];

export default function Buy() {
  const [params, setParams] = useSearchParams();
  const { t } = useI18n();
  const initialBrand = params.get("brand") || "";
  const initialQ = params.get("q") || "";

  const [filters, setFilters] = useState({
    brand: initialBrand, body_type: "", fuel_type: "", transmission: "",
    condition: "", location: "", price_min: "", price_max: "", year_min: "", year_max: "",
  });
  const [sort, setSort] = useState("newest");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState(initialQ);

  useEffect(() => {
    api.get("/brands").then(({ data }) => setBrands(data));
  }, []);

  const fetchListings = useMemo(() => async () => {
    setLoading(true);
    const cleaned = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null));
    try {
      const { data } = await api.get("/listings", {
        params: { listing_type: "sale", sort, limit: 60, ...cleaned },
      });
      let list = data.items || [];
      if (search) {
        const s = search.toLowerCase();
        list = list.filter(l =>
          `${l.brand} ${l.model} ${l.location}`.toLowerCase().includes(s)
        );
      }
      setItems(list);
    } finally { setLoading(false); }
  }, [filters, sort, search]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const updateFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ brand: "", body_type: "", fuel_type: "", transmission: "", condition: "", location: "", price_min: "", price_max: "", year_min: "", year_max: "" });

  return (
    <div data-testid="buy-page" className="bg-slate-50 min-h-screen">
      {/* Sticky search/filter */}
      <div className="sticky top-16 z-30 glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-cm-accent">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              data-testid="buy-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Toyota, Lexus, Honda…"
              className="flex-1 bg-transparent outline-none text-sm"
            />
          </div>
          <button data-testid="buy-toggle-filters" onClick={() => setShowFilters((v) => !v)} className="px-4 py-2.5 rounded-xl bg-cm-primary text-white text-sm font-medium flex items-center gap-2 press-scale">
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </button>
          <select data-testid="buy-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="hidden md:block px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm">
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low → High</option>
            <option value="price_desc">Price: High → Low</option>
            <option value="most_viewed">Most viewed</option>
          </select>
        </div>

        {/* Quick price pills */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-3 flex items-center gap-2 overflow-x-auto no-scrollbar">
          <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 flex-shrink-0 hidden sm:inline-flex items-center gap-1">
            <DollarSign className="w-3 h-3" /> Price
          </span>
          {PRICE_PILLS.map((p) => {
            const active = filters.price_min === p.min && filters.price_max === p.max;
            return (
              <button
                key={p.label}
                data-testid={`price-pill-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
                onClick={() => setFilters((f) => ({
                  ...f,
                  price_min: active ? "" : p.min,
                  price_max: active ? "" : p.max,
                }))}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-all ${active ? 'bg-cm-primary text-white border-cm-primary shadow-md' : 'bg-white text-slate-700 border-slate-200 hover:border-cm-accent hover:text-cm-primary'}`}
              >
                {p.label}
              </button>
            );
          })}
          {(filters.price_min || filters.price_max) && !PRICE_PILLS.some(p => filters.price_min === p.min && filters.price_max === p.max) && (
            <button
              onClick={() => setFilters((f) => ({ ...f, price_min: "", price_max: "" }))}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium bg-cm-accent text-white flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear price
            </button>
          )}
        </div>

        {showFilters && (
          <div className="border-t border-slate-200 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <FilterSelect testid="filter-brand" label={t("filter.brand")} value={filters.brand} onChange={(v) => updateFilter("brand", v)} options={brands.map(b => b.name)} />
              <FilterSelect testid="filter-body" label={t("filter.body")} value={filters.body_type} onChange={(v) => updateFilter("body_type", v)} options={BODY_TYPES} />
              <FilterSelect testid="filter-fuel" label={t("filter.fuel")} value={filters.fuel_type} onChange={(v) => updateFilter("fuel_type", v)} options={FUELS} />
              <FilterSelect testid="filter-transmission" label={t("filter.transmission")} value={filters.transmission} onChange={(v) => updateFilter("transmission", v)} options={TRANSMISSIONS} />
              <FilterSelect testid="filter-condition" label={t("filter.condition")} value={filters.condition} onChange={(v) => updateFilter("condition", v)} options={CONDITIONS} />
              <FilterSelect testid="filter-location" label={t("filter.location")} value={filters.location} onChange={(v) => updateFilter("location", v)} options={LOCATIONS} />
              <FilterInput testid="filter-price-min" label={t("filter.price_min")} type="number" value={filters.price_min} onChange={(v) => updateFilter("price_min", v)} />
              <FilterInput testid="filter-price-max" label={t("filter.price_max")} type="number" value={filters.price_max} onChange={(v) => updateFilter("price_max", v)} />
              <FilterInput testid="filter-year-min" label={t("filter.year_min")} type="number" value={filters.year_min} onChange={(v) => updateFilter("year_min", v)} />
              <FilterInput testid="filter-year-max" label={t("filter.year_max")} type="number" value={filters.year_max} onChange={(v) => updateFilter("year_max", v)} />
              <div className="md:col-span-4 flex items-center justify-between">
                <button data-testid="filter-clear" onClick={clearFilters} className="text-sm text-slate-600 hover:text-cm-primary flex items-center gap-1">
                  <X className="w-3.5 h-3.5" /> {t("buy.clear")}
                </button>
                <select data-testid="buy-sort-mobile" value={sort} onChange={(e) => setSort(e.target.value)} className="md:hidden px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm">
                  <option value="newest">Newest</option>
                  <option value="price_asc">Price ↑</option>
                  <option value="price_desc">Price ↓</option>
                  <option value="most_viewed">Popular</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 py-6">
        <div className="flex items-baseline justify-between mb-4 px-4 sm:px-0">
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-cm-primary">{t("buy.title")}</h1>
          <span data-testid="buy-result-count" className="text-sm text-slate-500">{items.length} {t("buy.results")}</span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 px-4 sm:px-0">
            {Array.from({ length: 6 }).map((_, i) => <div key={`sk-${i}`} className="aspect-[16/13] bg-slate-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div data-testid="buy-empty" className="text-center py-20 text-slate-500">{t("buy.empty")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 stagger">
            {items.map((l) => <CarCard key={l.id} listing={l} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:bg-white focus:border-cm-accent outline-none">
        <option value="">Any</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function FilterInput({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:bg-white focus:border-cm-accent outline-none" />
    </label>
  );
}
